import {
  Mutation,
  QueryClient,
  QueryKey,
  UseMutationResult as QRUseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query'
import axios, { AxiosError } from 'axios'
import { isEmpty, isEqual, xorWith } from 'lodash'
import React from 'react'
import { ApiError, ApiErrorData } from '../api'

export type QueryOptions<T> = Omit<
  UseQueryOptions<T, ApiError, T, QueryKey>,
  'queryKey' | 'queryFn' | 'initialData'
>

export type UseMutationResult<
  TData = unknown,
  TVariables = unknown,
  TContext = unknown,
> = QRUseMutationResult<TData, ApiError, TVariables, TContext>

// Query client
export const queryClient = new QueryClient({
  defaultOptions: {
    //TODO: mutations clear cache on Success (by first string in key)
    queries: {
      retry(failureCount, error: unknown) {
        if (axios.isAxiosError(error)) {
          const status = error.response?.status
          if (status !== undefined && status >= 400) return false
        }
        return failureCount <= 2
      },
    },
  },
})

// useQuery wrapper to get one resource doc
// Build the cache key like ['resourceName', '_ID']
// Is disabled when id is undefined
export function useResource<T>(resourceName: string, apiFn: (id: string) => Promise<T>, id?: string, params?: undefined, options?: QueryOptions<T>): UseQueryResult<T, ApiError>
export function useResource<T, P extends object>(resourceName: string, apiFn: (params: { id: string } & P) => Promise<T>, id?: string, params?: P, options?: QueryOptions<T>): UseQueryResult<T, ApiError>
export function useResource<T, P extends object | undefined>(
  resourceName: string,
  apiFn: (params: string | ({ id: string } & P)) => Promise<T>,
  id?: string,
  params?: P,
  options?: QueryOptions<T>,
): UseQueryResult<T, ApiError> {
  return useQuery<T, ApiError>(
    [resourceName, id],
    () => apiFn((params !== undefined ? { id: id!, ...params } as { id: string } & P : id!) as string | ({ id: string } & P)),
    { enabled: Boolean(id), ...options } as UseQueryOptions<T, ApiError>,
  )
}

// useQuery wrapper to fetch resource list docs
// Build the cache key like ['resourceName', { ...params } ]
export const useResources = <T, P = Record<string, unknown>>(
  resourceName: string,
  apiFn: (params: P) => Promise<T>,
  params: P,
  options?: QueryOptions<T>,
): UseQueryResult<T, ApiError> =>
  useQuery<T, ApiError>(
    [resourceName, params ?? {}],
    () => apiFn(params),
    options as UseQueryOptions<T, ApiError>,
  )

export type MutationState = Mutation<unknown, AxiosError<ApiErrorData, unknown>>['state']

// Get mutation cache state by key
export const useMutationState = (key: QueryKey): MutationState | undefined => {
  const [mutationState, setMutationState] = React.useState<MutationState>()
  React.useEffect(() => {
    return queryClient.getMutationCache().subscribe(data => {
      if (data.mutation && isEmpty(xorWith(key, data.mutation?.options.mutationKey, isEqual)))
        setMutationState(data.mutation.state)
    })
  }, [key])
  return mutationState
}
